import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import useSnackbar from '../../hooks/useSnackbar'
import { DashboardSignOff } from '../../types/gotypes'

interface ShareUpdateDialogProps {
  open: boolean
  onClose: () => void
  dashboardName: string
  shareId?: string
  shareEnabled: boolean
  /** Current dashboard sign-off, if any (#1156). Present and current → the
   *  message names the signer; absent (never signed off, or revoked) → the
   *  message says so. `signed_at` is unix MILLIS. */
  signOff?: DashboardSignOff
  /** Dashboard-level `config.needs_review` (#1156 amendment). With `signOff`
   *  set it selects the STALE variant — the sign-off no longer vouches for the
   *  numbers, but the record of who signed is kept. */
  needsReview?: boolean
  /** `config.data_refreshed_at` — latest wave ref across the dashboard's jobs,
   *  UTC RFC3339 string, computed on read. Dates the job update in the stale
   *  message; the same value dates the amber needs-review banner. */
  dataRefreshedAt?: string
  /** Dashboard `updated_at`, unix SECONDS (gorm autoUpdateTime) — anchors the
   *  "Last edited" line of the not-signed-off message (#1156). */
  lastEditedAt: number
  // Enables sharing on the dashboard (owner action) so the link works. Kept as
  // a callback so this component stays networking-free (page → hook → component).
  onEnableSharing?: () => Promise<void>
  enableSharingIsLoading?: boolean
}

const formatStamp = (unixMillis: number) =>
  new Date(unixMillis).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })

// ShareUpdateDialog is the "Share Update" flow (#974 H2, replacing the #964 v1
// stored-subscriber "Notify subscribers" dialog): no subscriber list, no stored
// recipients, no notification state — just a framed ready-to-send message with
// the real share link, and a one-click copy. The sender stays in control of
// who sees what and when. Note: the retired Notify Subscribers UI was the
// only thing that hit POST /:id/notify — the sole writer of in-app
// notifications (CreateUserNotifications) — so no NEW bell/ready-for-review
// entries are generated until a future trigger exists. The subscriber
// tables/endpoints and bell UI remain but are dormant, pending the
// notification front-door work (#1051).
const ShareUpdateDialog: React.FC<ShareUpdateDialogProps> = ({
  open,
  onClose,
  dashboardName,
  shareId,
  shareEnabled,
  signOff,
  needsReview,
  dataRefreshedAt,
  lastEditedAt,
  onEnableSharing,
  enableSharingIsLoading,
}) => {
  const snackbar = useSnackbar()
  const [copied, setCopied] = useState(false)

  const shareUrl = shareId ? `${window.location.origin}/dashboard/${shareId}` : ''
  // Three variants (#1156, amended — deliberate): signed off AND current names
  // the signer; stale keeps the history honest — it names the job update that
  // outdated the sign-off and the person who previously signed; never signed
  // off and revoked get the identical not-signed-off line. signed_at is
  // millis; updated_at is seconds; data_refreshed_at is a UTC RFC3339 string.
  const statusLine = signOff
    ? needsReview
      ? `Stale due to job update${dataRefreshedAt ? ` on ${formatStamp(Date.parse(dataRefreshedAt))}` : ''}. `
        + `Previously signed off by ${signOff.user_email} on ${formatStamp(signOff.signed_at)}.`
      : `Signed off by ${signOff.user_email} on ${formatStamp(signOff.signed_at)}.`
    : `Last edited ${formatStamp(lastEditedAt * 1000)} (not currently signed off).`
  const message = `Hi — the '${dashboardName}' dashboard has been updated and is ready for your review: ${shareUrl}\n${statusLine}`
  const linkReady = shareEnabled && Boolean(shareId)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message)
      setCopied(true)
      snackbar.success('Message copied — paste it wherever you like')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      snackbar.error('Could not copy to clipboard')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share Update</DialogTitle>
      <DialogContent dividers>
        {!linkReady && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              onEnableSharing && (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => onEnableSharing()}
                  disabled={enableSharingIsLoading}
                >
                  {enableSharingIsLoading ? <CircularProgress size={16} color="inherit" /> : 'Enable sharing'}
                </Button>
              )
            }
          >
            Sharing is off for this dashboard — the link below won't work until it's on.
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          value={message}
          InputProps={{ readOnly: true }}
          onFocus={e => e.target.select()}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Button
            variant="contained"
            color="info"
            startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
            onClick={handleCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Just copy this text and send it to whoever needs to view it (that way you're in control of
          who sees what and when).
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ShareUpdateDialog
